import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import MovieCard from "@/components/base/MovieCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import HotRanking from "@/pages/home/components/HotRanking";
import {
  getHotMovies,
  getLatestMovies,
  getUpcomingMovies,
} from "@/lib/data";
import { cinemas, cities, cinemaBrands } from "@/mocks/cinemas";
import { news } from "@/mocks/news";

export default function Home() {
  const heroMovies = getHotMovies(5);
  const hotMovies = getHotMovies(6);
  const latestMovies = getLatestMovies(8);
  const upcomingMovies = getUpcomingMovies(4);
  const featuredCinemas = cinemas.slice(0, 3);
  const topNews = news.slice(0, 3);

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="bg-primary-950 pb-10 pt-20 md:pb-14 md:pt-24">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-8">
          <div className="mb-6 mt-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 text-sm font-medium text-accent-400">
                <i className="ri-flashlight-fill"></i>
                最新电影排片 · 影院资讯一站式门户
              </p>
              <h1 className="mt-2 font-heading text-2xl font-bold text-background-50 md:text-3xl">
                九七电影院
              </h1>
            </div>
            <div className="hidden items-center gap-6 text-sm text-primary-200 md:flex">
              <span className="flex items-center gap-2">
                <i className="ri-film-line text-accent-400"></i>12 部在映影片
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-building-line text-accent-400"></i>9 家合作影院
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-calendar-check-line text-accent-400"></i>每日更新排片
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <HeroCarousel movies={heroMovies} />
            </div>
            <div className="lg:col-span-1">
              <HotRanking movies={hotMovies} />
            </div>
          </div>
        </div>
      </section>

      {/* 最新上映 */}
      <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <SectionHeading
          title="最新上映"
          subtitle="近期登陆大银幕的热门影片，点选海报查看详情与排片"
          moreTo="/movies"
        />
        <div
          data-product-shop
          className="grid grid-cols-2 gap-4 animate-fade-up sm:grid-cols-3 lg:grid-cols-4"
        >
          {latestMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* 影院排片快捷入口 */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-8">
          <SectionHeading
            title="影院排片快捷入口"
            subtitle="按城市或品牌快速找到身边影院，查看当日排片"
            moreTo="/cinemas"
            moreLabel="全部影院"
          />

          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">按城市：</span>
            {cities
              .filter((c) => c !== "全部")
              .map((city) => (
                <Link
                  key={city}
                  to={`/cinemas?city=${encodeURIComponent(city)}`}
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                >
                  {city}
                </Link>
              ))}
          </div>

          <div className="mb-8 flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">按品牌：</span>
            {cinemaBrands.map((brand) => (
              <Link
                key={brand}
                to={`/cinemas?brand=${encodeURIComponent(brand)}`}
                className="whitespace-nowrap rounded-full bg-secondary-100 px-4 py-1.5 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                {brand}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {featuredCinemas.map((cinema) => (
              <Link
                key={cinema.id}
                to={`/cinema/${cinema.id}`}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
              >
                <div className="h-40 w-full overflow-hidden bg-background-200">
                  <img
                    src={cinema.images[0]}
                    alt={`${cinema.name} 影院环境`}
                    title={`${cinema.name} 九七电影院`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="truncate font-heading text-sm font-semibold text-foreground-950">
                      {cinema.name}
                    </h3>
                    <span className="shrink-0 text-xs font-semibold text-accent-600">
                      {cinema.rating.toFixed(1)} 分
                    </span>
                  </div>
                  <p className="mt-2 flex items-center gap-1 truncate text-xs text-foreground-500">
                    <i className="ri-map-pin-line"></i>
                    {cinema.city} · {cinema.district}
                  </p>
                  <p className="mt-1 flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-phone-line"></i>
                    {cinema.phone}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 影讯快报 */}
      <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <SectionHeading
          title="影讯快报"
          subtitle="最新影评、行业动态与预告资讯"
          moreTo="/news"
          moreLabel="进入影讯专题"
        />
        <div className="grid grid-cols-1 gap-6 animate-fade-up lg:grid-cols-3">
          {topNews.map((article) => (
            <Link
              key={article.id}
              to={`/news?id=${article.id}`}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
            >
              <div className="h-44 w-full overflow-hidden bg-background-200">
                <img
                  src={article.cover}
                  alt={`${article.title} 配图`}
                  title={`${article.title} 九七电影院影讯`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-4">
                <span className="mb-2 inline-flex w-fit items-center rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-900">
                  {article.category}
                </span>
                <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 group-hover:text-primary-600 line-clamp-2">
                  {article.title}
                </h3>
                <p className="mt-2 flex-1 text-sm text-foreground-500 line-clamp-2">
                  {article.summary}
                </p>
                <p className="mt-3 text-xs text-foreground-400">
                  {article.date} · {article.author}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 即将上映 + 网站介绍 */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 gap-10 px-4 md:px-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeading title="即将上映" subtitle="提前了解下月新片，把握观影计划" moreTo="/movies" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {upcomingMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-information-line text-xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">关于九七电影院</h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              九七电影院是一个聚焦最新电影排片与影院资讯的信息门户。我们汇集多类型影片资料、影院排片与放映时间，帮助影迷快速获取影片资讯与购票指南。
            </p>
            <ul className="mt-4 space-y-2 text-sm text-foreground-600">
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-accent-600"></i>覆盖多城市合作影院
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-accent-600"></i>每日更新上映排片
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-accent-600"></i>多维度影片筛选
              </li>
            </ul>
            <Link
              to="/about"
              className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              了解更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}